import config from '../config/api';

class ApiService {
  constructor() {
    this.baseURL = config.API_URL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      ...this.defaultHeaders,
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle token refresh if needed
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry the original request with new token
          const newHeaders = {
            ...headers,
            ...this.getAuthHeaders(),
          };
          const retryResponse = await fetch(url, {
            ...options,
            headers: newHeaders,
          });
          return this.handleResponse(retryResponse);
        } else {
          // Refresh failed, redirect to login
          this.logout();
          throw new Error('Authentication failed');
        }
      }

      return this.handleResponse(response);
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // Handle API response
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // DRF returns errors in various formats: {message, detail, non_field_errors, field_name}
      const message = errorData.message
        || errorData.detail
        || (errorData.non_field_errors && errorData.non_field_errors[0])
        || Object.values(errorData).flat().join(', ')
        || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(message);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return response.text();
  }

  // Token refresh
  async refreshToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}${config.endpoints.AUTH_REFRESH}`, {
        method: 'POST',
        headers: this.defaultHeaders,
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access);
        if (data.refresh) {
          localStorage.setItem('refresh_token', data.refresh);
        }
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    return false;
  }

  // Authentication methods
  async login(username, password) {
    const response = await this.request(config.endpoints.AUTH_LOGIN, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    if (response.access) {
      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  }

  async register(userData) {
    const response = await this.request(config.endpoints.AUTH_REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.access) {
      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    return response;
  }

  async getProfile() {
    return this.request(config.endpoints.AUTH_PROFILE);
  }

  async updateProfile(data) {
    return this.request(config.endpoints.AUTH_PROFILE, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }

  // Brand methods
  async getBrands() {
    return this.request(config.endpoints.BRANDS);
  }

  async createBrand(brandData) {
    return this.request(config.endpoints.BRANDS, {
      method: 'POST',
      body: JSON.stringify(brandData),
    });
  }

  async getBrand(id) {
    return this.request(config.endpoints.BRAND_DETAIL(id));
  }

  async updateBrand(id, brandData) {
    return this.request(config.endpoints.BRAND_DETAIL(id), {
      method: 'PUT',
      body: JSON.stringify(brandData),
    });
  }

  async deleteBrand(id) {
    return this.request(config.endpoints.BRAND_DETAIL(id), {
      method: 'DELETE',
    });
  }

  async addBrandMember(brandId, username) {
    return this.request(config.endpoints.BRAND_ADD_MEMBER(brandId), {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
  }

  async removeBrandMember(brandId, username) {
    return this.request(config.endpoints.BRAND_REMOVE_MEMBER(brandId), {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
  }

  // Post methods
  async getPosts(params = {}) {
    const searchParams = new URLSearchParams(params);
    const endpoint = `${config.endpoints.POSTS}?${searchParams}`;
    return this.request(endpoint);
  }

  async createPost(postData) {
    if (postData instanceof FormData) {
      const url = `${this.baseURL}${config.endpoints.POSTS}`;
      const headers = { ...this.getAuthHeaders() };
      const response = await fetch(url, { method: 'POST', headers, body: postData });
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          const retryHeaders = { ...this.getAuthHeaders() };
          const retryResponse = await fetch(url, { method: 'POST', headers: retryHeaders, body: postData });
          return this.handleResponse(retryResponse);
        }
        this.logout();
        throw new Error('Authentication failed');
      }
      return this.handleResponse(response);
    }
    return this.request(config.endpoints.POSTS, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
  }

  async getPost(id) {
    return this.request(config.endpoints.POST_DETAIL(id));
  }

  async updatePost(id, postData) {
    return this.request(config.endpoints.POST_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(postData),
    });
  }

  async deletePost(id) {
    return this.request(config.endpoints.POST_DETAIL(id), {
      method: 'DELETE',
    });
  }

  async publishPost(id, platforms = []) {
    return this.request(config.endpoints.POST_PUBLISH(id), {
      method: 'POST',
      body: JSON.stringify({ platforms }),
    });
  }

  async generatePostContent(data) {
    return this.request(config.endpoints.GENERATE_CONTENT, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Cadence methods
  async getCadence(brandId) {
    return this.request(config.endpoints.BRAND_CADENCE(brandId));
  }

  async saveCadence(brandId, slots) {
    return this.request(config.endpoints.BRAND_CADENCE(brandId), {
      method: 'POST',
      body: JSON.stringify({ slots }),
    });
  }

  async generateWeek(brandId, weekStart, platform = null) {
    const body = { brand_id: brandId };
    if (weekStart) body.week_start = weekStart;
    if (platform) body.platform = platform;
    return this.request(config.endpoints.GENERATE_WEEK, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getCadenceRatio(brandId) {
    const params = new URLSearchParams({ brand_id: brandId });
    return this.request(`${config.endpoints.CADENCE_RATIO}?${params}`);
  }

  // Idea methods
  async getIdeas(params = {}) {
    const searchParams = new URLSearchParams(params);
    const endpoint = `${config.endpoints.IDEAS}?${searchParams}`;
    return this.request(endpoint);
  }

  async createIdea(data) {
    return this.request(config.endpoints.IDEAS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateIdea(id, data) {
    return this.request(config.endpoints.IDEA_DETAIL(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteIdea(id) {
    return this.request(config.endpoints.IDEA_DETAIL(id), {
      method: 'DELETE',
    });
  }

  // Influence Inbox methods
  async getInfluenceItems(params = {}) {
    const searchParams = new URLSearchParams(params);
    const endpoint = `${config.endpoints.INFLUENCE}?${searchParams}`;
    return this.request(endpoint);
  }

  async createInfluenceItem(data) {
    // If data is FormData (file upload), don't set Content-Type so browser adds multipart boundary
    if (data instanceof FormData) {
      const url = `${this.baseURL}${config.endpoints.INFLUENCE}`;
      const headers = { ...this.getAuthHeaders() };
      const response = await fetch(url, { method: 'POST', headers, body: data });
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          const retryHeaders = { ...this.getAuthHeaders() };
          const retryResponse = await fetch(url, { method: 'POST', headers: retryHeaders, body: data });
          return this.handleResponse(retryResponse);
        }
        this.logout();
        throw new Error('Authentication failed');
      }
      return this.handleResponse(response);
    }
    return this.request(config.endpoints.INFLUENCE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteInfluenceItem(id) {
    return this.request(config.endpoints.INFLUENCE_DETAIL(id), {
      method: 'DELETE',
    });
  }

  async convertToIdea(id, text = '') {
    return this.request(config.endpoints.INFLUENCE_CONVERT(id), {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // Social Account methods
  async getSocialAccounts(brandId = null) {
    const params = brandId ? { brand_id: brandId } : {};
    const searchParams = new URLSearchParams(params);
    const endpoint = `${config.endpoints.SOCIAL_ACCOUNTS}?${searchParams}`;
    return this.request(endpoint);
  }

  async deleteSocialAccount(accountId) {
    return this.request(config.endpoints.SOCIAL_ACCOUNT_DETAIL(accountId), {
      method: 'DELETE',
    });
  }

  async updateSocialAccount(accountId, data) {
    return this.request(config.endpoints.SOCIAL_ACCOUNT_DETAIL(accountId), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async connectPlatform(platform, connectionData) {
    const endpoints = {
      linkedin: config.endpoints.CONNECT_LINKEDIN,
      facebook: config.endpoints.CONNECT_FACEBOOK,
      instagram: config.endpoints.CONNECT_INSTAGRAM,
      x: config.endpoints.CONNECT_X,
    };

    return this.request(endpoints[platform.toLowerCase()], {
      method: 'POST',
      body: JSON.stringify(connectionData),
    });
  }

  async disconnectSocialAccount(id) {
    return this.request(config.endpoints.SOCIAL_ACCOUNT_DETAIL(id), {
      method: 'DELETE',
    });
  }

  // Analytics methods
  async getAnalytics(brandId = null) {
    const params = brandId ? { brand_id: brandId } : {};
    const searchParams = new URLSearchParams(params);
    const endpoint = `${config.endpoints.ANALYTICS}?${searchParams}`;
    return this.request(endpoint);
  }

  async getBrandAnalyticsSummary(brandId) {
    const searchParams = new URLSearchParams({ brand_id: brandId });
    const endpoint = `${config.endpoints.ANALYTICS_BRAND_SUMMARY}?${searchParams}`;
    return this.request(endpoint);
  }

  async refreshAnalytics(brandId) {
    return this.request(config.endpoints.REFRESH_ANALYTICS, {
      method: 'POST',
      body: JSON.stringify({ brand_id: brandId }),
    });
  }

  // Dashboard (Command Center)
  async getDashboard(brandId) {
    const params = new URLSearchParams({ brand_id: brandId });
    return this.request(`${config.endpoints.DASHBOARD}?${params}`);
  }

  // Pipeline
  async getPipeline(brandId) {
    const params = new URLSearchParams({ brand_id: brandId });
    return this.request(`${config.endpoints.PIPELINE}?${params}`);
  }

  // Calendar
  async getCalendarData(brandId, year, month) {
    const params = new URLSearchParams({ brand_id: brandId, year, month });
    return this.request(`${config.endpoints.CALENDAR_DATA}?${params}`);
  }

  // Analytics Insights
  async getAnalyticsInsights(brandId, days = 30) {
    const params = new URLSearchParams({ brand_id: brandId, days });
    return this.request(`${config.endpoints.ANALYTICS_INSIGHTS}?${params}`);
  }

  // Connections / Audience
  async getConnections(params = {}) {
    const searchParams = new URLSearchParams(params);
    return this.request(`${config.endpoints.CONNECTIONS}?${searchParams}`);
  }

  async getConnectionStats(brandId) {
    const params = new URLSearchParams({ brand_id: brandId });
    return this.request(`${config.endpoints.CONNECTIONS_STATS}?${params}`);
  }

  async importConnections(data) {
    if (data instanceof FormData) {
      const url = `${this.baseURL}${config.endpoints.CONNECTIONS_IMPORT}`;
      const headers = { ...this.getAuthHeaders() };
      const response = await fetch(url, { method: 'POST', headers, body: data });
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          const retryHeaders = { ...this.getAuthHeaders() };
          const retryResponse = await fetch(url, { method: 'POST', headers: retryHeaders, body: data });
          return this.handleResponse(retryResponse);
        }
        this.logout();
        throw new Error('Authentication failed');
      }
      return this.handleResponse(response);
    }
    return this.request(config.endpoints.CONNECTIONS_IMPORT, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async reclassifyConnections(brandId) {
    return this.request(config.endpoints.CONNECTIONS_RECLASSIFY, {
      method: 'POST',
      body: JSON.stringify({ brand_id: brandId }),
    });
  }

  // Utility methods
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
}

// Create and export a single instance
const apiService = new ApiService();
export default apiService;
